/**
 * Cart Status Announcer: announces every /cart/add (EventManager cart:item-added, dispatched
 * by cart-add-interceptor.js whichever cart UI handles it) through the visually hidden
 * role="status" #CartStatusLiveRegion, for WCAG 4.1.3 Status Messages, Level AA. A registered
 * cart dialog owns the add feedback instead, and announcements defer to it.
 */

window.UFT = window.UFT || {};
window.UFT.CartStatusAnnouncer =
	window.UFT.CartStatusAnnouncer ||
	(function () {
		const SELECTORS = {
			LIVE_REGION: '#CartStatusLiveRegion',
		};

		const TIMEOUTS = {
			// Clear-then-set delay, so identical consecutive messages re-announce.
			REANNOUNCE_DELAY_MS: 150,
			// How long each queued message holds the region before the next one clears it.
			MESSAGE_DWELL_MS: 1000,
			// A cart dialog's focus announcement IS the add feedback and would drop a competing
			// polite message, so hold this long for one to claim it; announce anyway if none does.
			DIALOG_GRACE_MS: 300,
		};

		const DECIMAL_RADIX = 10;

		// Caps the backlog, so a burst can't leave the region seconds behind the cart.
		const MAX_QUEUE_LENGTH = 4;

		class CartStatusAnnouncer {
			constructor() {
				this.liveRegion = null;
				// A set rather than a boolean: two dialogs can be live at once (UpCart and
				// <cart-drawer> through the dark-launch overlap), so either one's disconnect would
				// otherwise cancel the other's registration.
				this.cartDialogOwners = new Set();
				this.pendingAnnouncementTimer = null;
				// Message held on the grace timer, so a suppression can retain it.
				this.pendingMessage = null;
				// Message silenced in favor of a dialog's own announcement, kept for replay.
				this.suppressedMessage = null;
				this.suppressUntil = 0;
				this.messageQueue = [];
				this.queueDraining = false;
				this.boundHandleCartItemAdded = this.handleCartItemAdded.bind(this);
				this.init();
			}

			init() {
				if (this.subscribe()) {
					return;
				}

				// EventManager loads via a separate deferred script; retry once those have run.
				if (document.readyState === 'loading') {
					document.addEventListener('DOMContentLoaded', () => this.retryInit(), { once: true });
				} else {
					setTimeout(() => this.retryInit(), 0);
				}
			}

			retryInit() {
				if (this.subscribe()) {
					return;
				}

				window.UFT.FailureReporter?.report?.(
					'cart-status-announcer-no-event-manager',
					'CartStatusAnnouncer: UFT.EventManager unavailable after retry; add-to-cart is not announced to screen readers.'
				);
			}

			get cartDialogActive() {
				return this.cartDialogOwners.size > 0;
			}

			// owner keys the caller so two registrants can coexist; it defaults so an unkeyed
			// call still works.
			registerCartDialog(owner = 'default') {
				this.cartDialogOwners.add(owner);
			}

			// The in-flight suppression window is only dropped once NO owner is left, or one
			// drawer disconnecting would hand the keyboard back while another dialog is up.
			unregisterCartDialog(owner = 'default') {
				this.cartDialogOwners.delete(owner);
				if (this.cartDialogOwners.size === 0) {
					this.suppressUntil = 0;
				}
			}

			// Also suppresses the next add briefly, covering the race where the drawer opens just
			// before cart:item-added arrives. The silenced message is retained in case the dialog
			// never announces, and supersedes any earlier one so no stale add is replayed.
			suppressPendingAnnouncement() {
				this.suppressedMessage = this.pendingMessage;
				this.pendingMessage = null;
				this.clearPendingAnnouncement();
				this.suppressUntil = Date.now() + TIMEOUTS.DIALOG_GRACE_MS;
			}

			// For when the dialog decides not to announce after all (the shopper's focus was
			// already inside it): reopen the window and replay the silenced message.
			releasePendingAnnouncement() {
				this.suppressUntil = 0;
				const message = this.suppressedMessage || this.pendingMessage;
				this.suppressedMessage = null;
				this.pendingMessage = null;
				this.clearPendingAnnouncement();
				if (message) {
					this.announce(message);
				}
			}

			clearPendingAnnouncement() {
				if (this.pendingAnnouncementTimer) {
					clearTimeout(this.pendingAnnouncementTimer);
					this.pendingAnnouncementTimer = null;
				}
			}

			subscribe() {
				if (!window.UFT.EventManager) {
					return false;
				}

				const eventName = window.UFT.EventManager.eventStrings.CART_ITEM_ADDED;
				window.UFT.EventManager.on(eventName, this.boundHandleCartItemAdded);
				return true;
			}

			handleCartItemAdded(data) {
				const items = Array.isArray(data?.items) ? data.items : [];
				const message = this.buildMessage(items);
				if (!message) {
					return;
				}

				if (!this.cartDialogActive) {
					this.announce(message);
					return;
				}

				// Stay quiet, but keep the message: the dialog may end up not announcing.
				if (Date.now() < this.suppressUntil) {
					this.suppressedMessage = message;
					return;
				}

				this.holdAnnouncement(message);
			}

			holdAnnouncement(message) {
				this.clearPendingAnnouncement();
				this.pendingMessage = message;
				this.pendingAnnouncementTimer = setTimeout(() => {
					this.pendingAnnouncementTimer = null;
					this.pendingMessage = null;
					this.announce(message);
				}, TIMEOUTS.DIALOG_GRACE_MS);
			}

			buildMessage(items) {
				// Drop zero/invalid quantities, or a "0" coerces to 1 and announces a phantom add.
				const counted = items
					.map((item) => ({ item, quantity: this.normalizeQuantity(item?.quantity) }))
					.filter((entry) => entry.quantity !== null);

				if (counted.length === 0) {
					return this.reportUnannounceableAdd(items);
				}

				if (counted.length === 1) {
					return this.buildSingleItemMessage(counted[0].item, counted[0].quantity);
				}

				const totalQuantity = counted.reduce((sum, entry) => sum + entry.quantity, 0);
				return `${totalQuantity} items added to your cart.`;
			}

			// Warn only when items arrived but no quantity was usable: degraded, not broken.
			reportUnannounceableAdd(items) {
				if (items.length > 0) {
					window.UFT.FailureReporter?.report?.(
						'cart-status-announcer-quantity-shape',
						'CartStatusAnnouncer: cart:item-added carried items but none had a usable quantity; payload shape may have drifted.',
						{ level: 'warning', extra: { itemCount: items.length } }
					);
				}
				return null;
			}

			buildSingleItemMessage(item, quantity) {
				const name = item?.productName?.trim();

				if (!name) {
					return 'Item added to your cart.';
				}

				if (quantity > 1) {
					// "of" rather than the "×" glyph: screen readers pronounce × inconsistently.
					return `${quantity} of ${name} added to your cart.`;
				}

				return `${name} added to your cart.`;
			}

			normalizeQuantity(quantity) {
				const parsed = parseInt(quantity, DECIMAL_RADIX);
				return isNaN(parsed) || parsed < 1 ? null : parsed;
			}

			// priority marks a message the shopper must not miss: a reward they just unlocked, a
			// failure they need to act on. Routine progress chatter is not.
			announce(message, { priority = false } = {}) {
				if (!message) {
					return;
				}
				// A polite live region drops a message if the next one clears or overwrites it too
				// soon, so enqueue and drain one at a time, collapsing consecutive duplicates.
				if (this.messageQueue[this.messageQueue.length - 1]?.text === message) {
					return;
				}
				this.messageQueue.push({ text: message, priority });
				this.trimQueue();
				if (!this.queueDraining) {
					this.drainQueue();
				}
			}

			// Shed the oldest ROUTINE message, or a burst of quantity chatter evicts the one
			// announcement that mattered. Only an all-priority queue sheds the oldest priority one.
			trimQueue() {
				while (this.messageQueue.length > MAX_QUEUE_LENGTH) {
					const routineIndex = this.messageQueue.findIndex((entry) => !entry.priority);
					this.messageQueue.splice(routineIndex === -1 ? 0 : routineIndex, 1);
				}
			}

			drainQueue() {
				if (this.messageQueue.length === 0) {
					this.queueDraining = false;
					return;
				}

				const region = this.getLiveRegion();
				if (!region) {
					this.reportMissingLiveRegion();
					return;
				}

				this.queueDraining = true;
				this.paintNextMessage(region);
			}

			// The snippet renders on every brand, so a missing region means a real add died.
			reportMissingLiveRegion() {
				window.UFT.FailureReporter?.report?.(
					'cart-status-announcer-no-live-region',
					'CartStatusAnnouncer: live region #CartStatusLiveRegion missing; add-to-cart was not announced to screen readers.'
				);
				this.messageQueue = [];
				this.queueDraining = false;
			}

			paintNextMessage(region) {
				const message = this.messageQueue.shift().text;
				// Clear first so screen readers re-announce identical messages, then dwell so the
				// polite region reads this one before the next clear.
				region.textContent = '';
				setTimeout(() => {
					region.textContent = message;
					setTimeout(() => this.drainQueue(), TIMEOUTS.MESSAGE_DWELL_MS);
				}, TIMEOUTS.REANNOUNCE_DELAY_MS);
			}

			getLiveRegion() {
				if (!this.liveRegion || !this.liveRegion.isConnected) {
					this.liveRegion = document.querySelector(SELECTORS.LIVE_REGION);
				}
				return this.liveRegion;
			}
		}

		return new CartStatusAnnouncer();
	})();
